import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { fetchData } from "../../api/client";
import { getClientCommercialName } from "../../utils/clientName";

const fmtIdName = (c) =>
  `#${c?.id ?? "—"} ${getClientCommercialName(c) || c?.nome || c?.name || "Sem nome"}`;

function normalizeClients(list) {
  return Array.isArray(list) ? list.filter((c) => c && c.id != null) : [];
}

function pickPrimaryId(clients, preferredId = null) {
  const list = normalizeClients(clients);
  if (!list.length) return null;
  if (preferredId != null && list.some((c) => Number(c.id) === Number(preferredId))) {
    return Number(preferredId);
  }
  return Number(list[0].id);
}

function mergeOptionsUnique(existing, incoming) {
  const set = new Set([...(existing || []), ...(incoming || [])].map((v) => String(v ?? "")));
  return [...set].filter((v) => v !== "");
}

/**
 * MergeClientsDialog
 * - Unifica N clientes (2..5) em um `primaryId`, chamando /clients/merge/preview por secondary
 *   e /clients/merge/commit para cada secondary.
 */
export default function MergeClientsDialog({ open, clients, onClose, onMerged }) {
  const list = useMemo(() => normalizeClients(clients), [clients]);
  const [primaryId, setPrimaryId] = useState(() => pickPrimaryId(list));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [resolvedFields, setResolvedFields] = useState({});
  const [hasPreview, setHasPreview] = useState(false);
  const [previewSummary, setPreviewSummary] = useState(null);

  const secondaries = useMemo(
    () => list.filter((c) => Number(c.id) !== Number(primaryId)),
    [list, primaryId]
  );

  const mergeSelectionKey = useMemo(() => {
    if (!open || !primaryId || secondaries.length < 1) return "";
    return `${primaryId}|${secondaries.map((c) => c.id).join(",")}`;
  }, [open, primaryId, secondaries]);

  const lastAutoPreviewKeyRef = useRef("");

  useEffect(() => {
    if (!open) {
      lastAutoPreviewKeyRef.current = "";
      return;
    }
    // Importante: NÃO resetar o preview quando `clients` muda por refetch da lista,
    // senão o usuário vê os campos abrirem e "sumirem".
    setPrimaryId((prev) => pickPrimaryId(list, prev));
    setLoading(false);
    setError(null);
    setConflicts([]);
    setResolvedFields({});
    setHasPreview(false);
    setPreviewSummary(null);
    lastAutoPreviewKeyRef.current = "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Se a lista mudar (refetch) enquanto o modal está aberto, só garante que o `primaryId` continua válido.
  useEffect(() => {
    if (!open) return;
    setPrimaryId((prev) => pickPrimaryId(list, prev));
  }, [open, list]);

  const runPreview = async () => {
    if (!primaryId) return;
    if (secondaries.length < 1) return;
    setLoading(true);
    setError(null);
    setHasPreview(false);
    setPreviewSummary(null);
    try {
      const byField = new Map();
      const totals = { leads: 0, leadsmensagens: 0, pesquisasrespostas: 0 };
      for (const sec of secondaries) {
        const resp = await fetchData("/clients/merge/preview", "POST", {
          primaryId,
          secondaryId: Number(sec.id),
          patchCandidate: {},
        });
        const data = resp?.data || resp;
        const cs = Array.isArray(data?.conflicts) ? data.conflicts : [];
        const refs = data?.affectedRefs || {};
        totals.leads += Number(refs.leads) || 0;
        totals.leadsmensagens += Number(refs.leadsmensagens) || 0;
        totals.pesquisasrespostas += Number(refs.pesquisasrespostas) || 0;
        for (const c of cs) {
          if (!c?.field) continue;
          const prev = byField.get(c.field);
          if (!prev) {
            byField.set(c.field, {
              field: c.field,
              label: c.label || c.field,
              options: Array.isArray(c.options) ? c.options : [],
              recommended: c.recommended,
            });
          } else {
            byField.set(c.field, {
              ...prev,
              options: mergeOptionsUnique(prev.options, c.options),
              recommended: prev.recommended ?? c.recommended,
            });
          }
        }
      }

      const nextConflicts = [...byField.values()].sort((a, b) =>
        String(a.field).localeCompare(String(b.field))
      );
      setConflicts(nextConflicts);

      const nextResolved = {};
      nextConflicts.forEach((c) => {
        const opts = Array.isArray(c.options) ? c.options : [];
        nextResolved[c.field] = c.recommended ?? (opts.length ? opts[0] : "");
      });
      setResolvedFields(nextResolved);
      setPreviewSummary({
        conflictCount: nextConflicts.length,
        secondaryCount: secondaries.length,
        totals,
      });
      setHasPreview(true);
    } catch (e) {
      setError(e?.message || "Não foi possível preparar a unificação.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!mergeSelectionKey) return;
    if (lastAutoPreviewKeyRef.current === mergeSelectionKey) return;
    lastAutoPreviewKeyRef.current = mergeSelectionKey;
    runPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergeSelectionKey]);

  const commitMerge = async () => {
    if (!primaryId) return;
    if (secondaries.length < 1) return;
    setLoading(true);
    setError(null);
    try {
      for (const sec of secondaries) {
        await fetchData("/clients/merge/commit", "POST", {
          primaryId,
          secondaryId: Number(sec.id),
          resolvedFields: resolvedFields || {},
        });
      }
      onMerged?.({ primaryId });
    } catch (e) {
      setError(e?.message || "Não foi possível concluir a unificação.");
    } finally {
      setLoading(false);
    }
  };

  const closeDisabled = loading;
  const canPreview = !!primaryId && secondaries.length >= 1 && !loading;
  const canCommit = canPreview && hasPreview;
  const unifyDisabledReason =
    !canCommit && !loading
      ? hasPreview
        ? ""
        : "Aguarde a comparação dos campos ou clique em Comparar campos."
      : "";

  return (
    <Dialog open={open} onClose={closeDisabled ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Unificar clientes</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Você selecionou {list.length} clientes. Escolha qual cadastro será mantido (ID principal).
          </Typography>

          <Divider />

          <FormControl>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
              Cliente principal
            </Typography>
            <RadioGroup
              value={primaryId != null ? String(primaryId) : ""}
              onChange={(e) => {
                setPrimaryId(Number(e.target.value));
                setConflicts([]);
                setResolvedFields({});
                setHasPreview(false);
                setPreviewSummary(null);
                setError(null);
                lastAutoPreviewKeyRef.current = "";
              }}
            >
              {list.map((c) => (
                <FormControlLabel
                  key={c.id}
                  value={String(c.id)}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {fmtIdName(c)}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          <Divider />

          <Box>
            <Button variant="outlined" disabled={!canPreview} onClick={runPreview}>
              {loading ? (
                <CircularProgress size={18} />
              ) : hasPreview ? (
                "Comparação concluída — comparar novamente"
              ) : (
                "Comparar campos"
              )}
            </Button>
          </Box>

          {hasPreview ? (
            <Alert severity={conflicts.length ? "info" : "success"}>
              {conflicts.length
                ? `${conflicts.length} campo(s) com divergência entre os cadastros. Escolha o valor a manter abaixo.`
                : "Nenhum conflito entre os campos dos cadastros selecionados. Você pode concluir a unificação."}
              {previewSummary ? (
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                  Serão absorvidos {previewSummary.secondaryCount} cadastro(s) secundário(s).
                  {(previewSummary.totals.leads > 0 ||
                    previewSummary.totals.leadsmensagens > 0 ||
                    previewSummary.totals.pesquisasrespostas > 0) && (
                    <>
                      {" "}
                      Vínculos a migrar: {previewSummary.totals.leads} solicitação(ões),{" "}
                      {previewSummary.totals.leadsmensagens} mensagem(ns),{" "}
                      {previewSummary.totals.pesquisasrespostas} pesquisa(s).
                    </>
                  )}
                </Typography>
              ) : null}
            </Alert>
          ) : null}

          {conflicts.length ? (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Resolver divergências</Typography>
              {conflicts.map((c) => {
                const opts = Array.isArray(c.options) ? c.options : [];
                return (
                  <FormControl key={c.field} size="small" fullWidth>
                    <InputLabel>{c.label || c.field}</InputLabel>
                    <Select
                      label={c.label || c.field}
                      value={resolvedFields?.[c.field] ?? ""}
                      onChange={(e) =>
                        setResolvedFields((prev) => ({ ...(prev || {}), [c.field]: e.target.value }))
                      }
                    >
                      {opts.map((v) => (
                        <MenuItem key={`${c.field}-${String(v)}`} value={v}>
                          {String(v)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              })}
            </Stack>
          ) : null}

          {!hasPreview && !loading ? (
            <Typography variant="body2" color="text.secondary">
              A comparação dos campos é feita automaticamente. Quando concluída, o botão Unificar
              será habilitado.
            </Typography>
          ) : null}

          <Alert severity="info">
            Os cadastros secundários serão marcados como excluídos e os vínculos (solicitações,
            mensagens e pesquisas) serão movidos para o cliente principal.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={closeDisabled}>
          Fechar
        </Button>
        <Tooltip title={unifyDisabledReason || ""} disableHoverListener={!unifyDisabledReason}>
          <span>
            <Button variant="contained" onClick={commitMerge} disabled={!canCommit}>
              {loading ? <CircularProgress size={18} /> : "Unificar"}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}

